<script setup lang="ts">
  import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import type { IDraftListRow } from '@/domain/menu-import/draft-list';
  import { DRAFT_NAME_MAX } from '@/domain/menu-import/workspace-view';
  import type {
    IDraftAddonGroup,
    IDraftCategory,
    ILiveOther,
    IMenuDraft,
  } from '@/interfaces/IMenuImport';
  import { nextDomId } from '@/utils/dom-id.utils';
  import V2MenuDraftItem from './V2MenuDraftItem.vue';

  /**
   * One category of the workspace list (plan §4.5 «Category block»): a dark
   * left bar, the name (tap to rename inline, saved on Enter or blur), «{n}
   * platos · {m} para revisar», «Oculta» for a hidden copied category, and a ⋯
   * menu («Cambiar nombre», «Agregar plato aquí», «Quitar categoría»). A big
   * category with nothing to review starts collapsed. Off-screen dishes skip
   * rendering (`content-visibility`) unless one of them is open. In an
   * update (plan §3.5) it also counts the new prices, a category of dishes
   * that all stay as they are folds («Ver {count} platos sin cambios»), and
   * a category that is one of the menu's keeps its name (never published).
   */
  /** Estimated height of a collapsed-away dish row, for `contain-intrinsic-size`. */
  const ROW_ESTIMATE_PX = 84;

  const props = withDefaults(
    defineProps<{
      category: IDraftCategory;
      rows: IDraftListRow[];
      /** Every dish of the category, whatever the filter. */
      total: number;
      toCheck: number;
      /** Update sessions: its dishes whose price changes. */
      priceChanged?: number;
      /** Update sessions: its dishes already on the menu with nothing to change. */
      unchanged?: number;
      /** A category a proposal would add: shown, not editable. */
      ghost?: boolean;
      collapsed: boolean;
      addonGroups: Record<string, IDraftAddonGroup>;
      draft: IMenuDraft;
      openItemId?: string | null;
      selectedItemId?: string | null;
      currency: string;
      country: string;
      showReview: boolean;
      showAllergens: boolean;
      /** Opens the name for typing (a category just added). */
      startRename?: boolean;
      disabled?: boolean;
      /** Update sessions: the menu's dishes no dish is linked to (`draft.live.others`); else null. */
      liveOthers?: ILiveOther[] | null;
    }>(),
    {
      ghost: false,
      priceChanged: 0,
      unchanged: 0,
      openItemId: null,
      selectedItemId: null,
      startRename: false,
      disabled: false,
      liveOthers: null,
    }
  );
  const emit = defineEmits<{
    (e: 'rename', name: string): void;
    (e: 'remove'): void;
    (e: 'add-item'): void;
    (e: 'open', itemId: string): void;
    (e: 'expand'): void;
    (e: 'rename-started'): void;
    (e: 'confirm', itemId: string): void;
    (e: 'set-price', itemId: string, price: number): void;
    (e: 'hide', itemId: string): void;
    (e: 'link', itemId: string, liveItemId: number): void;
    (e: 'new', itemId: string): void;
    (e: 'keep-price', itemId: string): void;
    (e: 'keep-sizes', itemId: string): void;
    (e: 'show', itemId: string): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const trc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));
  const inputId = nextDomId('menu-category-name');

  const renaming = ref(false);
  const nameDraft = ref('');

  const update = computed(() => props.liveOthers !== null);
  /** «12 platos · 2 precios nuevos · 1 para revisar». */
  const countText = computed(() => {
    const parts = [
      trc('menuImport.review.categoryCount', props.total, {
        count: props.total,
      }),
    ];
    if (props.showReview && update.value && props.priceChanged > 0)
      parts.push(
        trc('menuImport.update.categoryChanged', props.priceChanged, {
          count: props.priceChanged,
        })
      );
    if (props.showReview && props.toCheck > 0)
      parts.push(
        tr('menuImport.review.categoryToCheck', { count: props.toCheck })
      );
    return parts.join(' · ');
  });
  /** One of the menu's categories: its name is never published, so it is not renamed here. */
  const canRename = computed(
    () =>
      !props.ghost &&
      !props.disabled &&
      props.category.liveCategoryId === undefined
  );
  /** «Ver {count} platos sin cambios» when every dish stays as it is. */
  const expandText = computed(() =>
    update.value && props.total > 0 && props.unchanged === props.total
      ? trc('menuImport.update.expandUnchanged', props.total, {
          count: props.total,
        })
      : trc('menuImport.review.expand', props.total, { count: props.total })
  );
  /** An open editor may hold popovers: its block renders normally. */
  const lazy = computed(
    () =>
      props.openItemId === null ||
      !props.rows.some((row) => !row.ghost && row.item.id === props.openItemId)
  );
  const rowsStyle = computed(() => ({
    '--rows-height': `${Math.max(props.rows.length, 1) * ROW_ESTIMATE_PX}px`,
  }));

  //#region Rename
  async function startRenaming() {
    if (!canRename.value) return;
    nameDraft.value = props.category.name;
    renaming.value = true;
    await nextTick();
    const input = document.getElementById(inputId);
    if (input instanceof HTMLInputElement) {
      input.focus();
      input.select();
    }
  }

  /** Enter or blur: the parent saves it when it is a valid, new name. */
  function commitRename() {
    if (!renaming.value) return;
    renaming.value = false;
    emit('rename', nameDraft.value);
  }

  function onRenameKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    renaming.value = false;
  }

  watch(
    () => props.startRename,
    (start) => {
      if (!start) return;
      emit('rename-started');
      void startRenaming();
    },
    { immediate: true }
  );
  //#endregion
</script>

<template>
  <section
    class="v2-menu-draft-category"
    :class="{ 'is-ghost': ghost, 'is-update': update }"
    :data-category-id="ghost ? undefined : category.id"
  >
    <header class="category-head">
      <span class="category-bar" aria-hidden="true" />
      <h2 v-show="!renaming" class="category-name">
        <button
          type="button"
          class="name-button"
          :disabled="!canRename"
          :aria-label="
            canRename
              ? `${category.name} · ${$t('menuImport.editor.rename')}`
              : undefined
          "
          @click="startRenaming"
        >
          {{ category.name }}
        </button>
      </h2>
      <PosUiInput
        v-if="renaming"
        :id="inputId"
        class="name-input"
        :value="nameDraft"
        :maxlength="DRAFT_NAME_MAX"
        :aria-label="$t('menuImport.editor.rename')"
        autocomplete="off"
        @input="nameDraft = $event"
        @enter="commitRename"
        @blur="commitRename"
        @keydown="onRenameKeydown"
      />
      <PosUiBadge v-if="category.hidden" size="sm">{{
        $t('menuImport.review.categoryHidden')
      }}</PosUiBadge>
      <PosUiBadge v-if="ghost" color="success" size="sm">{{
        $t('menuImport.proposal.new')
      }}</PosUiBadge>
      <span class="category-count">{{ countText }}</span>
      <PosUiPopover
        v-if="!ghost"
        class="category-menu"
        placement="bottom"
        align="end"
        :disabled="disabled"
      >
        <template #trigger="{ open, toggle }">
          <button
            type="button"
            class="menu-trigger"
            :aria-label="$t('menuImport.more')"
            :aria-expanded="open ? 'true' : 'false'"
            aria-haspopup="true"
            :disabled="disabled"
            @click="toggle"
          >
            <PosUiIcon name="dots-horizontal" :size="20" />
          </button>
        </template>
        <template #default="{ close }">
          <div class="menu-items">
            <PosUiButton
              v-if="canRename"
              color="neutral"
              variant="ghost"
              icon="pencil-outline"
              block
              class="menu-item"
              @click="
                close();
                startRenaming();
              "
              >{{ $t('menuImport.editor.rename') }}</PosUiButton
            >
            <PosUiButton
              color="neutral"
              variant="ghost"
              icon="plus"
              block
              class="menu-item"
              @click="
                close();
                emit('add-item');
              "
              >{{ $t('menuImport.editor.addItemHere') }}</PosUiButton
            >
            <PosUiButton
              color="error"
              variant="ghost"
              icon="delete-outline"
              block
              class="menu-item"
              @click="
                close();
                emit('remove');
              "
              >{{ $t('menuImport.editor.removeCategory') }}</PosUiButton
            >
          </div>
        </template>
      </PosUiPopover>
    </header>
    <div v-if="collapsed" class="category-collapsed">
      <PosUiButton
        color="neutral"
        variant="outline"
        icon="chevron-down"
        @click="emit('expand')"
        >{{ expandText }}</PosUiButton
      >
    </div>
    <div
      v-else-if="rows.length > 0"
      class="category-rows"
      :class="{ 'is-lazy': lazy }"
      :style="rowsStyle"
    >
      <V2MenuDraftItem
        v-for="row in rows"
        :key="row.key"
        :item="row.item"
        :before-item="row.before"
        :decoration="row.decoration"
        :ghost="row.ghost"
        :addon-groups="addonGroups"
        :draft="!row.ghost && row.item.id === openItemId ? draft : null"
        :open="!row.ghost && row.item.id === openItemId"
        :selected="!row.ghost && row.item.id === selectedItemId"
        :currency="currency"
        :country="country"
        :show-review="showReview"
        :show-allergens="showAllergens"
        :live-others="liveOthers"
        @open="emit('open', row.item.id)"
        @confirm="emit('confirm', row.item.id)"
        @set-price="emit('set-price', row.item.id, $event)"
        @hide="emit('hide', row.item.id)"
        @link="emit('link', row.item.id, $event)"
        @new="emit('new', row.item.id)"
        @keep-price="emit('keep-price', row.item.id)"
        @keep-sizes="emit('keep-sizes', row.item.id)"
        @show="emit('show', row.item.id)"
      />
    </div>
  </section>
</template>

<style scoped lang="scss">
  .v2-menu-draft-category {
    @apply tw-flex tw-flex-col tw-gap-2;
    .category-head {
      @apply tw-flex tw-min-h-11 tw-items-center tw-gap-2;
    }
    .category-bar {
      @apply tw-h-4 tw-w-[3px] tw-shrink-0 tw-rounded-sm tw-bg-neutral-900;
    }
    .category-name {
      @apply tw-m-0 tw-min-w-0 tw-text-sm tw-font-bold tw-uppercase tw-tracking-wide tw-text-neutral-900;
    }
    .name-button {
      @apply tw-min-h-11 tw-max-w-full tw-cursor-pointer tw-truncate tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-font-bold tw-uppercase tw-tracking-wide tw-text-neutral-900;
      &:disabled {
        @apply tw-cursor-default;
      }
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .name-input {
      @apply tw-min-w-0 tw-flex-1;
      max-width: 24rem;
    }
    .category-count {
      @apply tw-ml-auto tw-shrink-0 tw-text-xs tw-text-neutral-500;
    }
    .menu-trigger {
      @apply tw-flex tw-h-11 tw-w-11 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-neutral-600 hover:tw-bg-neutral-200;
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-400 hover:tw-bg-transparent;
      }
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .menu-items {
      @apply tw-flex tw-flex-col tw-gap-1;
    }
    .menu-item {
      @apply tw-justify-start;
    }
    .category-collapsed {
      @apply tw-flex;
    }
    .category-rows {
      // The padding keeps the rows' focus and selection rings inside the
      // containment box below (it clips its paint).
      @apply -tw-m-1 tw-flex tw-flex-col tw-gap-2 tw-p-1;
      // Rows far off screen skip layout and paint; a block with an open
      // editor renders normally (its popovers must not be contained).
      &.is-lazy {
        content-visibility: auto;
        contain-intrinsic-size: var(--rows-height);
        contain-intrinsic-size: auto var(--rows-height);
      }
    }
    &.is-ghost .category-bar {
      @apply tw-bg-emerald-600;
    }
    // Update sessions: the longer meta wraps under the name on phones.
    &.is-update {
      .category-head {
        @apply tw-flex-wrap md:tw-flex-nowrap;
      }
      // The name shrinks (truncated) instead of taking a line of its own.
      .category-name {
        @apply tw-flex-1 md:tw-flex-initial;
      }
      .category-count {
        @apply tw-order-last tw-shrink tw-basis-full tw-pl-[11px] md:tw-order-none md:tw-ml-auto md:tw-shrink-0 md:tw-basis-auto md:tw-pl-0;
      }
      .category-menu {
        @apply tw-ml-auto md:tw-ml-0;
      }
    }
  }
</style>
